import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  QBodyLayout,
  QBox,
  QFlex,
  QNotFound,
  QPropertiesPanel,
  QResourceRouteTabs,
  QRouteTabs,
  QSpinner,
  QStack,
  SearchResource,
  useCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';

import eventDetailsApi from '../../../api/eventDetails.api';
import relationsApi from '../../../api/relations.api';
import { EventProperties, QualityEventHeader } from '../../../components';
import ChangeOwnerModal from '../../../components/ChangeOwnerModal/ChangeOwnerModal';
import DigitalSignatureModal from '../../../components/DigitalSignatureModal/DigitalSignatureModal';
import EventEditModal from '../../../components/EventModal/EventEditModal';
import EventOpenModal from '../../../components/EventModal/EventOpenModal';
import QualityEventIncompleteRequiredFieldsAlert from '../../../components/QualityEventAlerts/IncompleteRequiredFieldsAlert';
import UpdateTemplateFieldsAlert from '../../../components/QualityEventAlerts/UpdateTemplateFieldsAlert';
import UpdateEventPropertiesModal from '../../../components/UpdateEventPropertiesModal/UpdateEventPropertiesModal';
import { EventPermissionsProvider } from '../../../context';
import { useDocTitle, useEventTaskDrawer, useInvalidateTasksOnDrawerEvents } from '../../../hooks';
import { useEventPageData } from '../../../hooks/useEventPageData';
import {
  EventTemplateFields,
  QualityEventCreate,
  QualityEventDetailsResponse,
  QualityEventStatus,
  TemplateFieldDisplayNameMap,
} from '../../../types';
import { DigitalSignature, DigitalSignatureInput } from '../../../types/digitalSignature';

import EscalateEventModal from '../../../components/EscalateEventModal/EscalateEventModal';
import * as DisplayStrings from '../../../displayStrings';
import { useCreateEvent } from '../../../hooks/useCreateEvent';
import { useUpdateEvent } from '../../../hooks/useUpdateEvent';
import { joinReadable } from '../../../utils/arrayUtils';
import { filterOwnersByPrivateTags, filterTags } from '../../../utils/eventUtils';
import { buildEventQri, qriDetailsAsSelectData } from '../../../utils/qriUtils';
import { DateTime } from 'luxon';
import { useReassignEvent } from '../../../hooks/useReassignEvent';

type QualityEventDetailsUrlParams = {
  eventId: string;
};

const QualityEventDetails: React.FC = () => {
  const { t } = useTranslation();
  const { companyId, userId } = useCurrentUser();
  const { eventId: eventIdFromParam } = useParams<keyof QualityEventDetailsUrlParams>() as QualityEventDetailsUrlParams;
  const { showToast } = useToastProvider();

  const [isEditEventModalOpen, setIsEditEventModalOpen] = useState<boolean>(false);
  const [isEscalateEventModalOpen, setIsEscalateEventModalOpen] = useState<boolean>(false);
  const [isChangeOwnerModalOpen, setIsChangeOwnerModalOpen] = useState<boolean>(false);
  const [isReopenModalOpen, setIsReopenModalOpen] = useState<boolean>(false);
  const [isOpenModalOpen, setIsOpenModalOpen] = useState<boolean>(false);
  const [isCloseEventModalOpen, setIsCloseEventModalOpen] = useState<boolean>(false);
  const [isCancelEventModalOpen, setIsCancelEventModalOpen] = useState<boolean>(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState<boolean>(false);
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState<boolean>(false);
  const [isUpdateEventPropertiesModalOpen, setIsUpdateEventPropertiesModalOpen] = useState<boolean>(false);

  const [eventId, setEventId] = useState<number>(NaN);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutateAsync: createEventMutate } = useCreateEvent();

  useEffect(() => {
    const handleIncorrectEventId = () => {
      navigate('/');
      showToast({
        status: 'error',
        description: `There was an error displaying the page for event with id ${eventIdFromParam}`,
      });
      return;
    };

    try {
      if (
        !eventIdFromParam ||
        Number.isNaN(parseInt(eventIdFromParam)) ||
        typeof parseInt(eventIdFromParam) !== 'number'
      ) {
        return handleIncorrectEventId();
      }
    } catch {
      return handleIncorrectEventId();
    }

    setEventId(parseInt(eventIdFromParam));
  }, [eventIdFromParam, navigate, showToast]);

  const {
    eventData,
    refetch,
    usersData,
    tagsData,
    userTZ,
    suppliersData,
    relatedDocumentsData,
    relatedEventsData,
    eventActionLogData,
    alertFetchingRelatedContent,
    isEventError,
  } = useEventPageData(eventId);

  const { data: relationData, refetch: refetchRelations } = useQuery(
    ['relations', companyId, eventId],
    () => relationsApi.get(companyId, Number(eventId)),
    {
      enabled: !Number.isNaN(eventId) && eventId > 0,
    },
  );

  const eventDetails = eventData?.issue;

  const eventQris = useMemo(() => [buildEventQri(companyId, eventId)], [companyId, eventId]);

  const newEventOwners = useMemo(() => {
    return { users: filterOwnersByPrivateTags(usersData ?? [], tagsData ?? [], eventDetails?.tags_ids ?? []) };
  }, [usersData, tagsData, eventDetails]);

  useDocTitle(eventDetails ? `${eventDetails.code} ${eventDetails.title} - Qualio` : 'Qualio');

  const { updateEventMutate } = useUpdateEvent(eventId);

  const handleEventEditSave = useCallback(
    async (payload: QualityEventCreate) => {
      if (!eventId) {
        return;
      }
      await updateEventMutate(payload);
      refetch().then();
      refetchRelations().then();
    },
    [eventId, refetch, refetchRelations, updateEventMutate],
  );

  const { reassignEventMutate, isEventReassignLoading } = useReassignEvent(eventId);

  const [searchParams, setSearchParams] = useSearchParams();
  const taskIdFromUrl = searchParams.get('task_id');
  const closeTaskDrawer = useCallback(() => {
    if (searchParams.get('task_id')) {
      searchParams.delete('task_id');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);
  useEventTaskDrawer(eventData, taskIdFromUrl, closeTaskDrawer);
  useInvalidateTasksOnDrawerEvents();

  const handleEventReassignSave = useCallback(
    async (payload: QualityEventCreate) => {
      if (!eventId) {
        return;
      }
      await reassignEventMutate(Number(payload.owner_id));
      refetch().then();
      refetchRelations().then();
    },
    [eventId, refetch, refetchRelations, reassignEventMutate],
  );

  const handleEscalateEvent = useCallback(
    async (payload: QualityEventCreate) => {
      await createEventMutate(payload, {
        onSuccess: () => {
          showToast({
            title: DisplayStrings.EventCreated,
            description: t('common.notifications.eventCreatedWithTitle', { title: payload.title }),
            status: 'success',
            'data-cy': 'qe-event-create-success-toast',
          });
        },
      });

      refetch().then();
      refetchRelations().then();
    },
    [refetch, refetchRelations, createEventMutate, showToast, t],
  );

  const handleCloseEvent = useCallback(
    async (payload: DigitalSignature) => {
      if (!eventId) {
        return;
      }
      await eventDetailsApi.closeEvent(companyId, eventId, payload);
      refetch().then();
      refetchRelations().then();
      queryClient.invalidateQueries({ queryKey: ['eventActionList', companyId, eventId] });
    },
    [companyId, eventId, queryClient, refetch, refetchRelations],
  );

  const handleCancelEvent = useCallback(
    async (payload: DigitalSignature) => {
      if (!eventId) {
        return;
      }
      await eventDetailsApi.cancelEvent(companyId, eventId, payload);
      refetch().catch((error) => {
        console.error('Error while refetching:', error);
      });
      refetchRelations().catch((error) => {
        console.error('Error while refetching relations:', error);
      });
      queryClient.invalidateQueries({ queryKey: ['eventActionList', companyId, eventId] });
    },
    [companyId, eventId, queryClient, refetch, refetchRelations],
  );

  const handleReopen = useCallback(
    async (payload: DigitalSignature) => {
      if (!eventId) {
        return;
      }
      await eventDetailsApi.reopenEvent(companyId, eventId, payload);
      refetch().then();
      refetchRelations().then();
    },
    [companyId, eventId, refetch, refetchRelations],
  );

  const handleReject = useCallback(
    async (payload: DigitalSignature) => {
      if (!eventId) {
        return;
      }

      await eventDetailsApi.rejectEvent(companyId, eventId, payload);
      refetch().then();
      refetchRelations().then();
      queryClient.invalidateQueries({ queryKey: ['eventActionList', companyId, eventId] });
    },
    [companyId, eventId, queryClient, refetch, refetchRelations],
  );

  const handleAccept = useCallback(
    async (payload: DigitalSignature) => {
      if (!eventId) {
        return;
      }

      await eventDetailsApi.acceptEvent(companyId, eventId, payload);
      refetch().catch((error) => {
        console.error('Error while refetching:', error);
      });
      refetchRelations().catch((error) => {
        console.error('Error while refetching relations:', error);
      });
    },
    [companyId, eventId, refetch, refetchRelations],
  );

  const handleOpen = useCallback(
    async (payload: QualityEventCreate) => {
      if (!eventId) {
        return;
      }
      const openEventData = {
        ...payload,
        ...{ status: 'open' },
      };

      await updateEventMutate(openEventData);
      refetch().then();
      refetchRelations().then();
    },
    [eventId, refetch, refetchRelations, updateEventMutate],
  );

  const closeEventModalInput: DigitalSignatureInput = {
    headingText: DisplayStrings.CloseEarly,
    subText: t('events.modals.closeEarlySubText'),
    submitButtonText: t('events.actions.closeEvent'),
    successToastHeader: t('common.labels.eventClosed'),
    successToastDescription: t('events.modals.eventSuccessfullyClosed', { eventCode: eventDetails?.code }),
    successToastDataCy: 'qe-event-close-success-toast',
  };

  const cancelEventModalInput: DigitalSignatureInput = {
    headingText: DisplayStrings.CancelEvent,
    subText: DisplayStrings.EventCancelSubText,
    submitButtonText: DisplayStrings.CancelEvent,
    successToastHeader: DisplayStrings.Canceled,
    successToastDescription: DisplayStrings.EventCanceledMessage,
    successToastDataCy: 'qe-event-cancel-success-toast',
  };

  const reopenEventModalInput: DigitalSignatureInput = {
    headingText: t('events.actions.reopenEvent'),
    subText: t('events.modals.reopenEventSubText'),
    submitButtonText: t('events.actions.reopenEvent'),
    successToastHeader: t('common.labels.eventReopened'),
    successToastDescription: t('events.modals.eventSuccessfullyReopened', { eventCode: eventDetails?.code }),
    successToastDataCy: 'qe-event-reopen-success-toast',
  };

  const rejectEventModalInput: DigitalSignatureInput = {
    headingText: DisplayStrings.Reject,
    subText: DisplayStrings.EventRejectSubText,
    submitButtonText: DisplayStrings.Reject,
    successToastHeader: DisplayStrings.Rejected,
    successToastDescription: DisplayStrings.EventRejectedMessage,
    successToastDataCy: 'qe-event-reject-success-toast',
  };

  const acceptEventModalInput: DigitalSignatureInput = {
    headingText: DisplayStrings.Accept,
    subText: DisplayStrings.EventAcceptSubText,
    submitButtonText: DisplayStrings.Accept,
    successToastHeader: DisplayStrings.Accepted,
    successToastDescription: DisplayStrings.EventAcceptedMessage,
    successToastDataCy: 'qe-event-accept-success-toast',
  };

  const handleEditEventClicked = useMemo(() => () => setIsEditEventModalOpen(true), []);

  const handleEditEventFromCloseToast = useMemo(
    () => () => {
      setIsCloseEventModalOpen(false);
      setIsEditEventModalOpen(true);
    },
    [],
  );

  const handleCloseEventApiEror = useMemo(
    () => (err: any) => {
      const rawData = err?.response?.data;
      const fields = Array.isArray(rawData) ? rawData : null;
      if (err?.response?.status === 406 && Array.isArray(fields)) {
        const fieldsMessage = joinReadable(fields.map((field) => <b key={`${field}`}>{field}</b>));
        showToast({
          title: t('common.notifications.missingInformation'),
          description: (
            <span>
              <>
                {fieldsMessage} field{fields.length > 1 ? 's' : ''} must be completed in order to close event.
              </>
              <button style={{ color: '#3D4CF2' }} onClick={handleEditEventFromCloseToast}>
                &nbsp;Edit event
              </button>
            </span>
          ),
          status: 'error',
        });
        return true;
      }
      return false;
    },
    [handleEditEventFromCloseToast, showToast, t],
  );
  const eventSteps = eventData?.steps;

  const allButLastStepIsComplete =
    eventSteps?.filter(({ status }) => {
      return status !== 'completed';
    }).length === 1;

  const incompleteRequiredFields = useMemo(() => {
    if (!allButLastStepIsComplete || !eventDetails || !eventDetails.template_fields) {
      return [];
    }
    const fieldIsIncomplete = (field?: number | string | string[] | null) => {
      if (!(field ?? false)) {
        return true;
      }
      if (Array.isArray(field) && field.length === 0) {
        return true;
      }
      return false;
    };
    const requiredFields = eventDetails.template_fields.filter(({ mandatory }) => mandatory);
    const incompleteRequiredFieldNames: string[] = requiredFields
      .filter(({ attribute_name }) => {
        switch (attribute_name) {
          case 'product':
            return fieldIsIncomplete(eventDetails.products?.map(({ matrix_id }) => matrix_id));
          case 'root_cause':
            return fieldIsIncomplete(eventDetails.rootcauses?.map(({ matrix_id }) => matrix_id));
          case 'related_to':
            console.error('Related to should not be required');
            return false;
          case 'supplier':
            return fieldIsIncomplete(eventDetails?.suppliers);
          case 'risk':
            return (
              fieldIsIncomplete(eventDetails.risk_id) ||
              fieldIsIncomplete(eventDetails.severity_id) ||
              fieldIsIncomplete(eventDetails.probability_id)
            );
          default:
            return fieldIsIncomplete(eventDetails[`${attribute_name}_id`]);
        }
      })
      .map(({ attribute_name }) => attribute_name);
    if (incompleteRequiredFieldNames.includes('risk')) {
      if (eventDetails?.severity_id == null) {
        incompleteRequiredFieldNames.push(TemplateFieldDisplayNameMap['severity']);
      }
      if (eventDetails?.probability_id == null) {
        incompleteRequiredFieldNames.push(TemplateFieldDisplayNameMap['probability']);
      }
      incompleteRequiredFieldNames.splice(incompleteRequiredFieldNames.indexOf('risk'), 1);
    }
    const incompleteRequiredFieldNamesDisplay = incompleteRequiredFieldNames.map((field) => {
      return field in TemplateFieldDisplayNameMap
        ? TemplateFieldDisplayNameMap[field as keyof typeof TemplateFieldDisplayNameMap]
        : field[0]?.toUpperCase() + field?.slice(1);
    });
    return incompleteRequiredFieldNamesDisplay;
  }, [eventDetails, allButLastStepIsComplete]);

  // The event couldn't be fetched (e.g. a 404 for a deleted / non-existent event, or another
  // fetch failure after retries). Show the not-found page instead of spinning forever.
  if (isEventError && !eventData) {
    return <QNotFound data-cy="event-not-found" />;
  }

  if (!eventData || !eventDetails || eventSteps === undefined) {
    return (
      <QFlex height="100vh" justify="center" align="center">
        <QStack direction="column" align="center">
          <QSpinner marginTop="-48" />
        </QStack>
      </QFlex>
    );
  }
  const suppliersTemplateField = eventDetails?.template_fields?.filter(
    ({ attribute_name }) => attribute_name === 'supplier',
  )[0];
  const suppliersConfiguration = {
    enabled: !!suppliersTemplateField,
    required: suppliersTemplateField?.mandatory ?? false,
  };

  const filteredTagsData = filterTags(userId, usersData ?? [], tagsData ?? []);

  const updateEventTemplateFields = (templateFields: EventTemplateFields) => {
    queryClient.setQueryData<QualityEventDetailsResponse | undefined>(['eventDetails', companyId, eventId], (old) =>
      old ? { ...old, issue: { ...old.issue, template_fields: templateFields } } : old,
    );
  };

  const shouldDisplaySyncAlert = () => {
    return eventId && eventDetails.status !== QualityEventStatus.enum.closed;
  };
  const shouldDisplayRequiredFieldsAlert = () => allButLastStepIsComplete && incompleteRequiredFields.length > 0;

  const due = eventDetails.due_date
    ? eventDetails.due_date
    : DateTime.fromSeconds(eventDetails.due_at).toFormat('yyyy-MM-dd');

  const header = (
    <QualityEventHeader
      code={eventDetails.code}
      title={eventDetails.title}
      status={eventDetails.status}
      due={due}
      updated_at={eventDetails.updated_at}
      description={eventDetails.description}
      onEditEventClicked={handleEditEventClicked}
      onChangeOwnerClicked={() => setIsChangeOwnerModalOpen(true)}
      onEscalateEventClicked={() => setIsEscalateEventModalOpen(true)}
      onReopenClicked={() => setIsReopenModalOpen(true)}
      onRejectClicked={() => setIsRejectModalOpen(true)}
      onAcceptClicked={() => setIsAcceptModalOpen(true)}
      onOpenClicked={() => setIsOpenModalOpen(true)}
      onCloseEventClicked={() => setIsCloseEventModalOpen(true)}
      onCancelEventClicked={() => setIsCancelEventModalOpen(true)}
      onUpdateEventPropertiesClicked={() => setIsUpdateEventPropertiesModalOpen(true)}
      eventId={eventId}
      companyId={companyId}
      userTZ={userTZ}
      alertFetchingRelatedContent={alertFetchingRelatedContent}
    />
  );

  const body = (
    <>
      <QFlex flexDirection={'column'} gap={4}>
        {shouldDisplayRequiredFieldsAlert() && (
          <QualityEventIncompleteRequiredFieldsAlert
            alertButtonAction={handleEditEventClicked}
            incompleteRequiredFields={incompleteRequiredFields}
          />
        )}
        {shouldDisplaySyncAlert() && (
          <UpdateTemplateFieldsAlert
            ownerId={eventData.issue.owner_id}
            updateEventTemplateFields={updateEventTemplateFields}
            eventTemplateId={eventData.issue.workflow_id}
            eventId={eventId}
            existingTemplateFields={eventData.issue.template_fields}
          />
        )}
      </QFlex>
      <QResourceRouteTabs qri={eventQris} showTasks={true} data-cy="event-details-tabs">
        <QRouteTabs.Item route="details" data-cy="details-tab">
          {t('events.tabs.details')}
        </QRouteTabs.Item>
      </QResourceRouteTabs>
      <QPropertiesPanel openOnMount>
        <EventProperties
          event={eventData}
          relatedSuppliers={suppliersData}
          relatedEvents={relatedEventsData}
          relatedDocuments={relatedDocumentsData}
          tags={tagsData}
          eventActionLog={eventActionLogData}
        />
      </QPropertiesPanel>
    </>
  );

  return (
    <EventPermissionsProvider userId={userId} event={eventDetails}>
      <QBodyLayout.Default>
        {header}
        <QBox>{body}</QBox>
      </QBodyLayout.Default>
      {isEditEventModalOpen && (
        <EventEditModal
          isOpen={isEditEventModalOpen}
          event={eventDetails}
          related={relationData ?? []}
          setIsOpen={setIsEditEventModalOpen}
          suppliers={qriDetailsAsSelectData(suppliersData)}
          relatedData={{
            relatedDocuments: qriDetailsAsSelectData(relatedDocumentsData),
            relatedEvents: qriDetailsAsSelectData(relatedEventsData),
          }}
          selectsData={{
            eventTags: filteredTagsData ?? [],
            users: usersData ?? [],
          }}
          onSave={handleEventEditSave}
        />
      )}
      <ChangeOwnerModal
        isOpen={isChangeOwnerModalOpen}
        event={eventDetails}
        setIsOpen={setIsChangeOwnerModalOpen}
        selectsData={newEventOwners}
        onSave={handleEventReassignSave}
        isEventUpdateLoading={isEventReassignLoading}
      />
      {isEscalateEventModalOpen && (
        <EscalateEventModal
          isOpen={isEscalateEventModalOpen}
          setIsOpen={setIsEscalateEventModalOpen}
          onSave={handleEscalateEvent}
          event={eventDetails}
          selectsData={{
            eventTags: filteredTagsData ?? [],
            users: usersData ?? [],
          }}
        />
      )}
      {isUpdateEventPropertiesModalOpen && (
        <UpdateEventPropertiesModal
          isOpen={isUpdateEventPropertiesModalOpen}
          event={eventDetails}
          setIsOpen={setIsUpdateEventPropertiesModalOpen}
          suppliersConfiguration={suppliersConfiguration}
          selectsData={{
            eventTags: filteredTagsData ?? [],
            relatedDocuments: qriDetailsAsSelectData(relatedDocumentsData),
            relatedEvents: qriDetailsAsSelectData(relatedEventsData),
            relatedSuppliers: qriDetailsAsSelectData(suppliersData),
            sourceEvents:
              eventDetails?.escalated_from?.map(({ title, id, code }) => ({
                label: `${code} ${title}`,
                code,
                title,
                value: String(id),
                resource: 'quality-event' as SearchResource,
              })) ?? [],
            escalatedTo:
              eventDetails?.escalated_to?.map(({ title, id, code }) => ({
                label: `${code} ${title}`,
                code,
                title,
                value: String(id),
                resource: 'quality-event' as SearchResource,
              })) ?? [],
          }}
          onSave={handleEventEditSave}
        />
      )}
      <DigitalSignatureModal
        isOpen={isCloseEventModalOpen}
        setIsOpen={setIsCloseEventModalOpen}
        onSave={handleCloseEvent}
        inputTexts={closeEventModalInput}
        isCommentRequired={true}
        onSubmitError={handleCloseEventApiEror}
      />
      <DigitalSignatureModal
        isOpen={isCancelEventModalOpen}
        setIsOpen={setIsCancelEventModalOpen}
        onSave={handleCancelEvent}
        inputTexts={cancelEventModalInput}
        isCommentRequired={true}
      />
      <DigitalSignatureModal
        isOpen={isReopenModalOpen}
        setIsOpen={setIsReopenModalOpen}
        onSave={handleReopen}
        inputTexts={reopenEventModalInput}
        isCommentRequired={false}
      />
      <DigitalSignatureModal
        isOpen={isRejectModalOpen}
        setIsOpen={setIsRejectModalOpen}
        onSave={handleReject}
        isCommentRequired={true}
        inputTexts={rejectEventModalInput}
        isSubmitButtonDestructive={true}
      />
      <DigitalSignatureModal
        isOpen={isAcceptModalOpen}
        setIsOpen={setIsAcceptModalOpen}
        onSave={handleAccept}
        isCommentRequired={false}
        inputTexts={acceptEventModalInput}
      />
      {isOpenModalOpen && (
        <EventOpenModal
          isOpen={isOpenModalOpen}
          event={eventDetails}
          related={relationData ?? []}
          setIsOpen={setIsOpenModalOpen}
          suppliers={qriDetailsAsSelectData(suppliersData)}
          selectsData={{
            eventTags: filteredTagsData ?? [],
            users: usersData ?? [],
          }}
          onSave={handleOpen}
        />
      )}
    </EventPermissionsProvider>
  );
};

export default QualityEventDetails;
